import { useCallback, useEffect, useState } from "react";

import { FEATURE_FLAG_LENS_TESTER_MODEL_SELECTION } from "../../../common/features";
import { useFeatureFlagEnabled } from "../../../common/posthog";
import { AVAILABLE_MODELS, AvailableModel, DEFAULT_CLAUDE_MODEL, PRODUCTION_MODEL } from "../../../services/claude";
import { LensHistoryItem } from "../utils/lensHistoryStorage";
import { TestLensConfig } from "./useLensTest";

const DEFAULT_TOKEN_COUNT = 3000;
const DEFAULT_TEMPERATURE = 0.1;

export interface AvailableModelOption {
  id: AvailableModel;
  name: string;
}

interface UseLensFormReturn {
  testLens: string;
  setTestLens: (value: string) => void;
  model: AvailableModel;
  setModel: (value: AvailableModel) => void;
  availableModels: readonly AvailableModelOption[];
  tokenCount: number;
  setTokenCount: (value: number) => void;
  temperature: number;
  setTemperature: (value: number) => void;
  lensName: string;
  setLensName: (value: string) => void;
  getFormConfig: (companyName: string, companyDescription: string) => TestLensConfig;
  resetForm: () => void;
}

const useLensForm = (historyItem: LensHistoryItem | null, initiallyEmpty: boolean = false): UseLensFormReturn => {
  const experimentalModelsEnabled = useFeatureFlagEnabled(FEATURE_FLAG_LENS_TESTER_MODEL_SELECTION);
  const availableModels: readonly AvailableModelOption[] = experimentalModelsEnabled ? AVAILABLE_MODELS : [PRODUCTION_MODEL];
  const defaultModel: AvailableModel = experimentalModelsEnabled ? DEFAULT_CLAUDE_MODEL : PRODUCTION_MODEL.id;
  const [testLens, setTestLens] = useState<string>("");
  const [model, setModel] = useState<AvailableModel>(defaultModel);
  const [tokenCount, setTokenCount] = useState<number>(DEFAULT_TOKEN_COUNT);
  const [temperature, setTemperature] = useState<number>(DEFAULT_TEMPERATURE);
  const [lensName, setLensName] = useState<string>("");

  useEffect(() => {
    if (historyItem && !initiallyEmpty) {
      setLensName(historyItem.name);
      setTestLens(historyItem.content);
      setModel(historyItem.model);
      setTokenCount(historyItem.tokens);
      setTemperature(historyItem.temperature);
    }
  }, [historyItem, initiallyEmpty]);

  useEffect(() => {
    if (historyItem?.isNamed) {
      setLensName(historyItem.name);
    }
  }, [historyItem]);

  const getFormConfig = useCallback((companyName: string, companyDescription: string): TestLensConfig => ({
    testLens, model, tokens: tokenCount, temperature, companyName, companyDescription, lensName
  }), [testLens, model, tokenCount, temperature, lensName]);

  const resetForm = useCallback((): void => {
    setTestLens("");
    setModel(defaultModel);
    setTokenCount(DEFAULT_TOKEN_COUNT);
    setTemperature(DEFAULT_TEMPERATURE);
    setLensName("");
  }, [defaultModel]);

  return {
    testLens, setTestLens, model, setModel, availableModels, tokenCount, setTokenCount,
    temperature, setTemperature, lensName, setLensName, getFormConfig, resetForm
  };
};

export { useLensForm };
